import { useEffect, useState } from 'react';
import { clsx } from 'clsx';

import { rpc } from '…/app/common/rpc/client.mts';
import { FLAGS } from '…/app/common/permissions/Features/FLAGS.mts';
import { useFeatureFlag } from '…/app/common/permissions/Features/useFeatureFlag.mts';
import { useAdminRole } from '…/app/common/permissions/useRoles.mjs';

import { IIcon } from '…/app/w/workspace/common/Icon/Icon.tsx';
import { formatDate } from '…/app/common/dateUtils.mts';
import { WidgetConfigBase } from '…/app/common/widget.mts';

import { HivebriteCredentialsDialog } from './HivebriteCredentialsDialog.tsx';
import { DeleteIntegrationDialog } from './DeleteIntegrationDialog.tsx';

import classes from '../Section.module.css';
import parentClasses from '../WorkspaceIntegrationsPage.module.css';

export interface Integration {
  id: string;
  accountType: string;
  createdAt: string;
  credentials?: {
    clientId?: string;
    apiUrl?: string;
  };
  syncLists?: SyncList[];
  permissions?: {
    enableContactNumOptinForms: boolean;
    enableContactsLastOptinFormAt: boolean;
    enableCreateContacts: boolean;
    enableUpdateContacts: boolean;
  };
}

export interface SyncList {
  displayName: string;
  remoteId: string;
}

export interface RemoteList {
  id: string;
  name: string;
}

export interface AddIntegrationListParams {
  displayName: string;
  integrationId: string;
  remoteId: string;
}

export interface DeleteIntegrationListParams {
  integrationId: string;
  remoteId: string;
}

interface WidgetConfig extends WidgetConfigBase {
  name: 'settings';
  data?: {
    lists: RemoteList[];
    onAdd: (params: AddIntegrationListParams) => void;
    onUnlink: (params: DeleteIntegrationListParams) => void;
    settings: Integration;
  };
}

interface IntegrationsSectionProps {
  className?: string;
  integrations: Map<string, Integration>;
  setWidgetConfig: (config: WidgetConfig | undefined) => void;
  addIntegrationList: (params: AddIntegrationListParams) => void;
  deleteIntegrationList: (params: DeleteIntegrationListParams) => void;
}

const convertIntegrationsToMap = (_integrations: Integration[]): Map<string, Integration> => (
  new Map(_integrations.map(integration => [integration.accountType.toUpperCase(), integration]))
);

export function IntegrationsSection({
  className,
  integrations,
  setWidgetConfig,
  addIntegrationList,
  deleteIntegrationList,
}: IntegrationsSectionProps) {
  const isIntegrationsEnabled = useFeatureFlag([FLAGS.workspaceIntegrations]);
  const isHivebriteEnabled = useFeatureFlag([FLAGS.workspaceHivebriteIntegration]);
  const userIsAdmin = useAdminRole();

  const hasIntegration = integrations?.size > 0;

  const [remoteLists, setRemoteLists] = useState<RemoteList[]>([]);
  const [showHivebriteDialog, setShowHivebriteDialog] = useState<boolean>(false);
  const [refreshIntegrations, setRefreshIntegrations] = useState<boolean>(false);
  const [integrationToDelete, setIntegrationToDelete] = useState<Integration | null>(null);

  // Create a map of the integrations for the existing integrations
  const integrationsMap = convertIntegrationsToMap(Array.from(integrations.values()));

  const fetchIntegrationLists = async () => {
    if (hasIntegration) {
      const integrationId = Array.from(integrations.keys())[0];
      const response = await rpc.post('actions/integration-get-lists', {
        json: { integrationId },
      });
      const responseData = await response.json() as { lists: RemoteList[] };
      setRemoteLists(responseData.lists);
    }
  };

  useEffect(() => {
    if (isIntegrationsEnabled) {
      fetchIntegrationLists();
    }
  }, [hasIntegration, isIntegrationsEnabled, refreshIntegrations]);

  return (
    <section className={clsx('Card', className)}>
      <div className={clsx(parentClasses.Content)}>
        <h2>Third-Party Integrations</h2>

        {!isIntegrationsEnabled && (
          <p>You currently don&apos;t have access to use CRM Integrations.</p>
        )}

        <div className={clsx(classes.SplitContent6040, 'StackableSplit')}>
          <div className={parentClasses.SectionSplit}>
            {[
              'Hubspot',
              'Salesforce',
              'Hivebrite',
            ].map((accountType) => {
              const integration: Integration | undefined = integrationsMap.get(accountType.toUpperCase());

              // Rows are disabled by default. Only the (1) active integration row is enabled OR the
              // (2) hivebrite row as it is self-serve.
              let rowIsEnabled = false;
              if (accountType === 'Hivebrite') {
                rowIsEnabled = isHivebriteEnabled;
              } else {
                // Salesforce and Hubspot are only enabled if there is an integration.
                rowIsEnabled = hasIntegration && !!integration;
              }

              return (
                <div
                  className={clsx(
                    classes.Row,
                    !rowIsEnabled ? classes.RowDisabled : null,
                  )}
                  key={`integration-${accountType}`}
                >
                  <div>
                    <div className={parentClasses.IntegrationName}>
                      <IIcon label={accountType} name={accountType.toLowerCase() as any} />

                      <h4>{accountType}</h4>
                    </div>

                    {integration && (
                      <>
                        <span className={parentClasses.SubText}>
                          Connected: {formatDate(integration.createdAt, null, true)}
                        </span>

                        {integration?.credentials?.clientId && (
                          <span className={parentClasses.SubText}>
                            Client ID: {integration.credentials.clientId}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex">
                    {/** Show the settings button for the integration (Salesforce, Hubspot) */}
                    {integration && accountType !== 'Hivebrite' && (
                      <button
                        className={parentClasses.IconButton}
                        disabled={!remoteLists?.length}
                        onClick={() => setWidgetConfig({
                          data: {
                            lists: remoteLists,
                            onAdd: addIntegrationList,
                            onUnlink: deleteIntegrationList,
                            settings: integration,
                          },
                          key: 'settings',
                          name: 'settings',
                          type: 'drawer',
                        })}
                      >
                        <IIcon label={`${accountType} Settings`} name="settings" />
                      </button>
                    )}

                    {/** Show the delete button for Hivebrite integration */}
                    {integration && accountType === 'Hivebrite' && (
                      <button
                        className={parentClasses.IconButtonDelete}
                        disabled={!userIsAdmin}
                        onClick={() => setIntegrationToDelete(integration)}
                      >
                        <IIcon label={`Delete ${accountType} integration`} name="trash" />
                      </button>
                    )}

                    {/** Show the add Hivebrite button */}
                    {isHivebriteEnabled && accountType === 'Hivebrite' && !integration && (
                      <button
                        className={parentClasses.IconButton}
                        disabled={!userIsAdmin}
                        onClick={() => setShowHivebriteDialog(true)}
                      >
                        <IIcon label="Add Hivebrite integration" name="settings" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className={clsx(parentClasses.SectionSplit, parentClasses.Instructions)}>
            <p>
              Seamlessly connect your CRM data with our new two-way integrations.{' '}

              To start, select an integration and authorize the connection.{' '}
              You will then be able to configure how data is synchronized.
            </p>
          </div>
        </div>
      </div>

      {isHivebriteEnabled && (
        <>
          <HivebriteCredentialsDialog
            open={showHivebriteDialog}
            setOpen={setShowHivebriteDialog}
            setRefresh={setRefreshIntegrations}
          />

          <DeleteIntegrationDialog
            integration={integrationToDelete}
            open={!!integrationToDelete}
            setOpen={setIntegrationToDelete}
            setRefresh={setRefreshIntegrations}
          />
        </>
      )}
    </section>
  );
}

IntegrationsSection.displayName = 'IntegrationsSection';
